/**
 * Raider bands: people who fight like people. A band is a group with roles that lives at a camp
 * (a map object of type "camp"), walks a road (a patrol), or lies in wait (an ambush). Bands
 * notice you by sight and sound, raise the alarm, run when they're losing, sometimes give up,
 * and break when their leader falls. A camp that's cleared stays quiet for a while, then fills
 * up again.
 *
 * Content is data: a camp in the map names one of these kinds in `props.band`.
 */
import type { CreatureKind } from './creatures.js';
import type { FactionId } from './factions.js';
import type { ItemId } from './items.js';
import type { Drop } from './creatures.js';

export type BandRole = 'leader' | 'fighter' | 'archer' | 'dog';
export type BandMode = 'camp' | 'patrol' | 'ambush';

export interface BandMember { mob: CreatureKind; role: BandRole; n: number }
export interface BandDef {
  name: string;
  faction: FactionId;
  mode: BandMode;
  members: BandMember[];
  /** Minutes a cleared band stays gone (a range, picked at random). */
  quiet: [number, number];
  /** What the camp's stash holds (camps only): taken once per player per occupation. */
  stash?: Drop[];
  /** A quest item that's in the stash for anyone on the step that needs it. */
  questItem?: ItemId;
  /** Shouted when the alarm goes up. */
  shout: string;
  /** Logged to everyone nearby when it's cleared. */
  cleared: string;
  /** Standing earned with each faction for clearing it. */
  clearStanding?: Partial<Record<FactionId, number>>;
  /** How hard it keeps watch: suspicion builds this many times as fast (a stronghold keeps a proper one). Default 1. */
  watch?: number;
}

export const BANDS_DEF = {
  lake_outpost: {
    name: 'Lake-road outpost', faction: 'reavers', mode: 'camp',
    members: [{ mob: 'reaver_sergeant', role: 'leader', n: 1 }, { mob: 'reaver', role: 'fighter', n: 3 }, { mob: 'reaver_archer', role: 'archer', n: 2 }, { mob: 'reaver_dog', role: 'dog', n: 1 }],
    quiet: [20, 30],
    stash: [{ item: 'coins', n: [40, 90] }, { item: 'iron_bar', n: [1, 2] }, { item: 'tinned_beans', n: [2, 3] }, { item: 'iron_tips', n: [15, 30] }],
    questItem: 'drift_ledger',
    shout: '"Over there! Gristle! We\'ve got company!"',
    cleared: 'The lake-road outpost goes quiet. For now.',
    clearStanding: { settlers: 20, drift: 10 },
  },
  copperback_dig: {
    name: 'Copperback dig', faction: 'reavers', mode: 'camp',
    members: [{ mob: 'reaver_foreman', role: 'leader', n: 1 }, { mob: 'goblin', role: 'archer', n: 2 }, { mob: 'reaver', role: 'fighter', n: 3 }],
    quiet: [20, 30],
    stash: [{ item: 'coins', n: [50, 110] }, { item: 'iron_ore', n: [4, 8] }, { item: 'copper_ore', n: [6, 10] }, { item: 'iron_bar', n: [1, 3] }],
    shout: '"Diggers! Drop the picks, there\'s someone on the spoil!"',
    cleared: 'The Copperback dig is abandoned, picks still in the ground.',
    clearStanding: { settlers: 15 },
  },
  saltreach_wreckers: {
    name: "Wreckers' hull", faction: 'reavers', mode: 'camp',
    members: [{ mob: 'wrecker_captain', role: 'leader', n: 1 }, { mob: 'wrecker', role: 'fighter', n: 4 }, { mob: 'reaver_archer', role: 'archer', n: 1 }],
    quiet: [20, 30],
    stash: [{ item: 'coins', n: [70, 140] }, { item: 'bottle' }, { item: 'raw_crab', n: [2, 4] }, { item: 'bog_pearl' }],
    shout: '"Lights! Someone\'s at the hull!"',
    cleared: 'The wreckers scatter from Saltreach. The false fire gutters out.',
    clearStanding: { settlers: 15, drift: 15 },
  },
  lake_patrol: {
    name: 'Reaver patrol', faction: 'reavers', mode: 'patrol',
    members: [{ mob: 'reaver_scout', role: 'leader', n: 1 }, { mob: 'reaver', role: 'fighter', n: 1 }, { mob: 'reaver_dog', role: 'dog', n: 1 }],
    quiet: [8, 12],
    shout: '"Patrol! On the road!"',
    cleared: 'The patrol won\'t be walking this road for a while.',
    clearStanding: { settlers: 8 },
  },
  shore_patrol: {
    name: 'Wrecker patrol', faction: 'reavers', mode: 'patrol',
    members: [{ mob: 'wrecker', role: 'leader', n: 1 }, { mob: 'reaver_archer', role: 'archer', n: 1 }],
    quiet: [8, 12],
    shout: '"Oi! You!"',
    cleared: 'The wrecker patrol is gone from the shore road.',
    clearStanding: { settlers: 8 },
  },
  cart_ambush: {
    name: 'Ambush', faction: 'reavers', mode: 'ambush',
    members: [{ mob: 'reaver', role: 'leader', n: 1 }, { mob: 'reaver', role: 'fighter', n: 1 }, { mob: 'reaver_archer', role: 'archer', n: 1 }],
    quiet: [10, 15],
    shout: '"Now! Get them!"',
    cleared: 'Nobody else is hiding behind the cart.',
    clearStanding: { settlers: 10 },
  },

  /* ---------------- the outer band: Rustcliff is the Reavers' home ground ---------------- */
  rustcliff_hold: {
    name: 'Rustcliff Hold', faction: 'reavers', mode: 'camp',
    members: [{ mob: 'rook', role: 'leader', n: 1 }, { mob: 'reaver_brute', role: 'fighter', n: 2 }, { mob: 'reaver_raider', role: 'fighter', n: 3 }, { mob: 'reaver_marksman', role: 'archer', n: 3 }, { mob: 'reaver_mastiff', role: 'dog', n: 2 }],
    quiet: [25, 35],
    stash: [{ item: 'coins', n: [300, 600] }, { item: 'steel_bar', n: [2, 4] }, { item: 'rail_steel', n: [4, 8] }, { item: 'copper_wire', n: [6, 12] }, { item: 'steel_tips', n: [30, 60] }, { item: 'tinned_beans', n: [3, 5] }],
    questItem: 'station_log',
    watch: 2,
    shout: '"In the yard! Mother! Someone\'s in the yard!"',
    cleared: 'Rustcliff Hold falls quiet. The yard is empty but for the wind in the wagons. It won\'t stay that way.',
    clearStanding: { settlers: 40, drift: 25 },
  },
  grove_cutters: {
    name: 'Ironwood cutters', faction: 'reavers', mode: 'camp',
    members: [{ mob: 'cutter_boss', role: 'leader', n: 1 }, { mob: 'reaver_cutter', role: 'fighter', n: 3 }, { mob: 'reaver_marksman', role: 'archer', n: 1 }, { mob: 'reaver_mastiff', role: 'dog', n: 1 }],
    quiet: [20, 30],
    stash: [{ item: 'coins', n: [140, 280] }, { item: 'ironwood_log', n: [4, 8] }, { item: 'yew_log', n: [2, 5] }, { item: 'steel_bar', n: [1, 2] }, { item: 'bowstring', n: [2, 4] }],
    shout: '"Axes down! Someone\'s in the cut!"',
    cleared: 'The cutters are gone from Hollow Grove. Stumps, sawdust and a half-felled ironwood are all they leave.',
    clearStanding: { settlers: 25 },
  },
  cove_smugglers: {
    name: "Smugglers' landing", faction: 'reavers', mode: 'camp',
    members: [{ mob: 'smuggler_boss', role: 'leader', n: 1 }, { mob: 'smuggler', role: 'fighter', n: 4 }, { mob: 'reaver_marksman', role: 'archer', n: 1 }],
    quiet: [20, 30],
    stash: [{ item: 'coins', n: [160, 320] }, { item: 'old_coin', n: [3, 8] }, { item: 'bottle', n: [2, 4] }, { item: 'raw_lobster', n: [3, 5] }, { item: 'cedar_log', n: [2, 4] }],
    shout: '"Douse the light! We\'ve been seen!"',
    cleared: 'The smugglers\' boats push off without them. Stillwater is still again.',
    clearStanding: { settlers: 20, drift: 30 },
  },
  rustcliff_patrol: {
    name: 'High-road patrol', faction: 'reavers', mode: 'patrol',
    members: [{ mob: 'reaver_outrider', role: 'leader', n: 1 }, { mob: 'reaver_raider', role: 'fighter', n: 1 }, { mob: 'reaver_mastiff', role: 'dog', n: 1 }],
    quiet: [10, 15],
    shout: '"Toll\'s due! All of it!"',
    cleared: 'Nobody is taking tolls on the high road. For now.',
    clearStanding: { settlers: 15, drift: 10 },
  },
  grove_patrol: {
    name: 'Cutters\' escort', faction: 'reavers', mode: 'patrol',
    members: [{ mob: 'reaver_cutter', role: 'leader', n: 1 }, { mob: 'reaver_marksman', role: 'archer', n: 1 }],
    quiet: [10, 15],
    shout: '"Harl! We\'ve got a tree-hugger!"',
    cleared: 'The cutters\' escort won\'t be walking the Grove for a while.',
    clearStanding: { settlers: 10 },
  },
} satisfies Record<string, BandDef>;

export type BandKind = keyof typeof BANDS_DEF;
export const isBandKind = (v: unknown): v is BandKind => typeof v === 'string' && Object.prototype.hasOwnProperty.call(BANDS_DEF, v);
export const bandDef = (k: BandKind): BandDef => BANDS_DEF[k] as BandDef;

/* ---------------- noticing people ---------------- */
/** How far a raider sees by day, at night, and how close is too close to miss. */
export const SIGHT_DAY = 9;
export const SIGHT_NIGHT = 5;
export const NOTICE_ALWAYS = 2;
/** Half the field of view, in radians (a raider sees 140 degrees ahead). */
export const SIGHT_HALF_ANGLE = (70 * Math.PI) / 180;
/** Suspicion (0..100) where a raider turns to look, and where it raises the alarm. */
export const SUSPICIOUS_AT = 35;
export const ALARM_AT = 100;
/** How far a fight can be heard, and how far running feet. */
export const HEAR_FIGHT = 9;
export const HEAR_RUN = 4;

export type Mood = 'calm' | 'wary' | 'alert' | 'flee' | 'yield';

/**
 * How much suspicion a raider gains this tick from seeing someone `d` tiles away. Sneaking
 * cuts it hard (and shortens how far you're seen); running makes it worse.
 */
export function suspicionGain(d: number, sight: number, sneaking: boolean, running: boolean): number {
  const reach = sneaking ? sight * 0.55 : sight;
  if (d > reach) return 0;
  const base = 9 + 26 * (1 - d / reach);
  return base * (sneaking ? 0.3 : 1) * (running ? 1.6 : 1);
}

/** Is (x, z) inside the view cone of someone at (ox, oz) facing `face` (the client's atan2(dx, dz))? */
export function inView(ox: number, oz: number, face: number, x: number, z: number): boolean {
  const a = Math.atan2(x - ox, z - oz);
  let d = Math.abs(a - face) % (Math.PI * 2);
  if (d > Math.PI) d = Math.PI * 2 - d;
  return d <= SIGHT_HALF_ANGLE;
}
